import { StopOutlined } from '@ant-design/icons'
import { Button, ColorPicker, Input, List, Switch, Typography } from 'antd'
import { DrilldownTypeAndFieldSelect } from 'features/drilldown/components/fieldSelect'
import { useDrilldownTableConfig } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import {
  DefaultFieldRenderProps,
  DrilldownTableColumnDef,
} from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  LinkProps,
  StringRenderer,
  TagRenderer,
} from 'features/drilldown/components/table/TableValueRenderers'
import { useGetPrecedingFilters } from 'features/drilldown/data/DrilldownFilterContext'
import { useNestedSetter } from 'features/drilldown/utils/useNestedSetter'
import { ValueSetterPair } from 'features/drilldown/utils/valueSetterPair'
import {
  OntologyContextType,
  OntologyField,
  OntologyRolodexEntryType,
  useFieldValues,
  useOntology,
} from 'features/dynamicOntology'
import find from 'lodash/find'
import first from 'lodash/first'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'
import {
  ChangeEvent,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import styled from 'styled-components'
import { GeckoEntry } from 'types'

import { extractTableValueFromEntry } from './explorerTableCellValues'

const { Text } = Typography

function convertLinkTemplateToTanstack(template: string): string {
  return template.replace(/\{([^}]+)\}/g, (_, fieldName) => `$${fieldName}`)
}

function buildParamsObject(
  linkTemplate: string,
  fieldValues: Record<string, string>
): Record<string, string> {
  const fieldNames = extractFieldsFromTemplate(linkTemplate)
  const params: Record<string, string> = {}
  fieldNames.forEach((fieldName) => {
    const value = fieldValues[fieldName]
    if (value === undefined) {
      throw new Error(`Field "${fieldName}" not found in fieldValues.`)
    }
    params[fieldName] = value
  })
  return params
}

function renderLinkTemplate(
  linkTemplate: string,
  fieldValues: Record<string, string>
): Pick<LinkProps, 'to' | 'params'> {
  const to = convertLinkTemplateToTanstack(linkTemplate)
  const params = buildParamsObject(linkTemplate, fieldValues)
  return { to, params }
}

export function extractFieldsFromTemplate(
  linkTemplate: string
): ReadonlyArray<OntologyField> {
  const matches = linkTemplate.match(/\{([^}]+)\}/g)
  if (!matches) return []
  return matches.map((match) => match.slice(1, -1))
}

function createLinkProps(
  { linkTemplate }: DrilldownTableColumnDef,
  entryPool: ReadonlyArray<GeckoEntry>,
  schemaFields: OntologyContextType['fields']
): LinkProps | undefined {
  if (!linkTemplate) return undefined
  const fieldValues = new Map(
    extractFieldsFromTemplate(linkTemplate)
      .map((field) => {
        const fieldDef = schemaFields[field]
        if (!fieldDef) {
          console.error(`No def in schema link-template field ${field}`)
          return undefined
        }
        const { entryType } = fieldDef
        const entry = find(entryPool, (e) => e.type === entryType)
        if (!entry) {
          console.warn(
            `No entry found in pool for link-template field ${field}`
          )
          return undefined
        }
        const fieldValue = extractTableValueFromEntry(entry, fieldDef)
        if (!fieldValue) {
          console.error(
            `Couldn't find a value in entry ${entry} for link-template field: ${field}`
          )
          return undefined
        }
        return [field, fieldValue]
      })
      .filter((val): val is [OntologyField, string] => !!val)
  )
  let renderedTemplate = undefined
  try {
    renderedTemplate = renderLinkTemplate(
      linkTemplate,
      Object.fromEntries([...fieldValues.entries()])
    )
  } catch {
    renderedTemplate = undefined
  }
  return renderedTemplate
    ? {
        ...renderedTemplate,
        target: '_blank',
      }
    : undefined
}

function extractFieldValues(
  entries: ReadonlyArray<GeckoEntry>,
  columnDef: DrilldownTableColumnDef,
  schemaFields: OntologyContextType['fields']
): ReadonlyArray<
  | {
      value: string
      copyValue?: string
      linkProps?: LinkProps
      shouldTruncate: boolean
    }
  | undefined
> {
  const valueFieldDef = schemaFields[columnDef.field]

  if (!valueFieldDef) {
    console.error(`No field def found for value field: ${columnDef.field}`)
  }

  const copyFieldDef = columnDef.copyField
    ? schemaFields[columnDef.copyField]
    : undefined
  return entries
    .filter((e) => e.type === valueFieldDef?.entryType)
    .map((e) => {
      if (!valueFieldDef) {
        return undefined
      }
      const value = extractTableValueFromEntry(e, valueFieldDef)
      if (!value) {
        return undefined
      }
      const copyValue = copyFieldDef
        ? extractTableValueFromEntry(e, copyFieldDef)
        : undefined

      // To render the links, you need to get the related entries
      // so, if I have an indication, I need to get it's linked unit
      // etc.
      const entryPool = [
        e,
        ...entries.filter(
          (linked) => !!e.tags[linked.type] && linked.id === e.tags[linked.type]
        ),
      ]

      return {
        value,
        copyValue,
        shouldTruncate: columnDef.truncateValue ?? true,
        linkProps: columnDef.linkTemplate
          ? createLinkProps(columnDef, entryPool, schemaFields)
          : undefined,
      }
    })
}

export const DefaultRender: FC<
  DefaultFieldRenderProps & {
    entries: ReadonlyArray<GeckoEntry>
  }
> = ({ columnDef, entries, groupByType }) => {
  const { fields: schemaFields } = useOntology()
  const { tagColor } = columnDef
  const fieldValues = useMemo(
    () => extractFieldValues(entries, columnDef, schemaFields),
    [columnDef, entries, schemaFields]
  )

  const allowMultiple = useDoAllowMultiple()(
    groupByType,
    schemaFields[columnDef.field]?.entryType ?? groupByType
  )

  const firstValue = first(fieldValues)

  return allowMultiple || tagColor
    ? fieldValues.map((fieldValue, idx) => {
        if (!fieldValue) {
          return <></>
        }
        const { value, linkProps } = fieldValue
        return (
          <TagRenderer
            key={idx}
            value={value}
            color={typeof tagColor === 'object' ? tagColor[value] : tagColor}
            linkProps={linkProps}
          />
        )
      })
    : firstValue && (
        <StringRenderer
          value={firstValue.value}
          linkProps={firstValue.linkProps}
        />
      )
}

const ConfigPanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 600px;
  align-items: start;
  gap: 0.5rem;
`

const ConfigPanelRow = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  & > :first-child {
    width: 150px; /* Set the desired width for the first child */
  }

  & > .ant-input {
    flex-grow: 1;
    width: auto;
  }

  & > .ant-input-affix-wrapper {
    flex-grow: 1;
    width: auto;
  }
`

export function useDoAllowMultiple() {
  const { isDescendant } = useOntology()
  return (
    groupByType: OntologyRolodexEntryType,
    entryType: OntologyRolodexEntryType
  ) => !isDescendant(groupByType, entryType)
}

export const DefaultRenderConfigPanel: FC<
  ValueSetterPair<DrilldownTableColumnDef, 'columnDef'>
> = ({ columnDef, setColumnDef }) => {
  const { copyField, title, tagColor, linkTemplate, field } = columnDef
  const setValueField = useNestedSetter(setColumnDef, 'field')
  const setCopyField = useNestedSetter(setColumnDef, 'copyField')
  const setTitle = useNestedSetter(setColumnDef, 'title')
  const setTagColor = useNestedSetter(setColumnDef, 'tagColor')
  const setLinkTemplate = useNestedSetter(setColumnDef, 'linkTemplate')

  const setNonNullCopyField = useCallback(
    (val: SetStateAction<string>) => {
      setCopyField((prev) => {
        const newVal = typeof val === 'function' ? val(prev ?? '') : val
        return isEqual(prev, newVal) ? prev : newVal
      })
    },
    [setCopyField]
  )

  const { groupByType } = useDrilldownTableConfig()
  const { fields, isDescendant } = useOntology()
  const { entryType, type: fieldType } = fields[field]
  const doAllowMultiple = useDoAllowMultiple()
  const allowMultiple = doAllowMultiple(groupByType, entryType)

  useEffect(() => {
    if (fieldType === 'enum' && typeof tagColor !== 'object') {
      setTagColor({})
    } else if (fieldType !== 'enum' && typeof tagColor === 'object') {
      setTagColor(undefined)
    }
  }, [fieldType, setTagColor, tagColor])

  useEffect(() => {
    setTitle((prev) => {
      const defaultFieldTitles = Object.values(fields).map(({ name }) => name)
      if (defaultFieldTitles.includes(prev)) {
        return fields[field]?.name ?? prev
      }
      return prev
    })
  }, [field, fields, setTitle])

  const allowedFields = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fields).filter(([k]) => {
          const kType = fields[k]?.entryType
          return (
            kType &&
            (isDescendant(kType, groupByType) ||
              isDescendant(groupByType, kType))
          )
        })
      ),
    [fields, groupByType, isDescendant]
  )

  return (
    <ConfigPanelContainer>
      <ConfigPanelRow>
        <Text>Title: </Text>
        <Input
          value={title}
          onChange={useCallback(
            (e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value),
            [setTitle]
          )}
        />
      </ConfigPanelRow>
      <ConfigPanelRow>
        <Text>Value Field: </Text>
        <DrilldownTypeAndFieldSelect
          selectedField={field}
          setSelectedField={(f) => setValueField(f)}
          fields={allowedFields}
        />
      </ConfigPanelRow>
      <ConfigPanelRow>
        <Text>Copy Field: </Text>
        <Switch
          checked={!!copyField}
          onChange={useCallback(
            (checked: boolean) => setCopyField(checked ? field : undefined),
            [field, setCopyField]
          )}
        />
        {copyField && (
          <DrilldownTypeAndFieldSelect
            selectedField={copyField!}
            fields={allowedFields}
            setSelectedField={setNonNullCopyField}
          />
        )}
      </ConfigPanelRow>
      <ConfigPanelRow>
        <Text>Tag Color: </Text>
        {typeof tagColor === 'object' ? (
          <EnumTagColorSelect
            field={field}
            colors={tagColor}
            setColors={setTagColor}
          />
        ) : (
          <ColorPicker
            value={tagColor}
            onChange={(color) => setTagColor(color.toHexString())}
          />
        )}
        {!allowMultiple && (
          <ClearButton
            disabled={!tagColor}
            onClick={() => setTagColor(undefined)}
          />
        )}
      </ConfigPanelRow>
      <ConfigPanelRow>
        <Text>Link Template: </Text>
        <Input
          value={linkTemplate}
          onChange={useCallback(
            (e: ChangeEvent<HTMLInputElement>) =>
              setLinkTemplate(e.target.value),
            [setLinkTemplate]
          )}
          allowClear
        />
      </ConfigPanelRow>
    </ConfigPanelContainer>
  )
}

const ClearButton: FC<{ disabled?: boolean; onClick: () => void }> = ({
  disabled,
  onClick,
}) => (
  <Button shape='circle' size='small' disabled={disabled} onClick={onClick}>
    <StopOutlined />
  </Button>
)

const EnumTagColorSelect: FC<{
  field: OntologyField
  colors: Record<string, string>
  setColors: Dispatch<
    SetStateAction<Record<string, string> | string | undefined>
  >
}> = ({ field, colors, setColors }) => {
  const precedingFilters = useGetPrecedingFilters()
  const { values: fieldValues, isLoading } = useFieldValues(field, {
    filters: precedingFilters,
  })

  const getColorSetter = useCallback<
    (fieldValue: string) => Dispatch<SetStateAction<string | undefined>>
  >(
    (fieldValue) => (color) => {
      setColors((_prev) => {
        const prev = typeof _prev === 'object' ? _prev : undefined
        const prevVal = prev?.[fieldValue]
        const newVal = typeof color === 'function' ? color(prevVal) : color
        if (newVal === prevVal) {
          return prev
        }
        if (newVal) {
          return {
            ...prev,
            [fieldValue]: newVal,
          }
        }
        return omit(prev, fieldValue)
      })
    },
    [setColors]
  )

  return (
    <div>
      <List
        loading={isLoading}
        bordered
        dataSource={[...fieldValues]}
        locale={{ emptyText: 'No columns.' }}
        renderItem={(fieldValue) => (
          <List.Item
            key={fieldValue}
            actions={[
              <ColorPicker
                key='picker'
                value={colors[fieldValue]}
                onChange={(color) =>
                  getColorSetter(fieldValue)(color.toHexString())
                }
              />,
              <ClearButton
                key='clear_button'
                disabled={!colors[fieldValue]}
                onClick={() => getColorSetter(fieldValue)(undefined)}
              />,
            ]}>
            {fieldValue}
          </List.Item>
        )}
      />
    </div>
  )
}
